import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
    postPrimaryDataset,
    type PrimaryDatasetKind,
} from '~/backend/connector-next-api/primaryDatasetClient';

export type PrimaryDatasetChange = {
    kind: PrimaryDatasetKind;
    /** The dataset to designate, or null to clear the designation. */
    datasetId: string | null;
};

export type SetPrimaryDataset = {
    setPrimaryDataset: (change: PrimaryDatasetChange) => void;
    isSaving: boolean;
    /** True after a failed write, until the next attempt. */
    isError: boolean;
};

/**
 * Designates a patient's primary planning image or structure set, guarded by
 * the patient's current version (If-Match), and refreshes what the worklist
 * shows.
 *
 * The detail carries the designations and the list rows do not, but both are
 * invalidated through the shared `['api', 'patients']` prefix: the version the
 * next write is guarded by comes from the detail, so a stale detail would make
 * the second designation conflict against the first.
 */
export function useSetPrimaryDataset(
    patientId: string,
    version: string | undefined
): SetPrimaryDataset {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({ kind, datasetId }: PrimaryDatasetChange) => {
            if (version === undefined) {
                // Nothing to guard the write with, so it is not attempted: an
                // unconditional POST would overwrite a designation someone
                // else made while this panel was loading.
                return Promise.reject(
                    new Error('The patient version is not known yet.')
                );
            }
            return postPrimaryDataset(patientId, kind, datasetId, version);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['api', 'patients'] }),
    });

    const { mutate } = mutation;
    const setPrimaryDataset = useCallback(
        (change: PrimaryDatasetChange) => mutate(change),
        [mutate]
    );

    return {
        setPrimaryDataset,
        isSaving: mutation.isPending,
        isError: mutation.isError,
    };
}
